/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * Lab curves for the category grid's "versus yours" sparklines: the same ClearBuy RPC the
 * PDP calls (`lib/dealhunt/audioChart`), fetched once per lab id per page load and shared by
 * every card that needs it (the declared pair's curves are wanted by every tested card).
 * Fetches are deferred until a card is in view — a category page can hold a dozen tested
 * products and most are never scrolled to.
 */
import { useEffect, useRef, useState, type RefObject } from 'react'
import {
  loadLabChartsOrThrow,
  LAB_PEER_COLORS,
  LAB_PRIMARY_COLOR,
  type LabChartPaths,
} from 'lib/dealhunt/audioChart'

/** The declared pair's line colour: the lab's red, the warm colour nearest the orange the cards
 *  print the reader's numbers in. `LabData` gives it to the pair on the PDP deep link too. */
export const YOURS_COLOR = LAB_PEER_COLORS[1]
/** This product — the lab's primary-curve cyan. */
export const THIS_COLOR = LAB_PRIMARY_COLOR
/** Query param the lab dialog's "Open the full lab data" link sets; LabData pre-selects that lab
 *  id. */
export const LAB_COMPARE_PARAM = 'lab-compare'
/** The lab section's anchor on the PDP (ProductPageTemplate `#sec-labs`). */
export const LAB_ANCHOR = 'sec-labs'

/** The PDP's lab section with the declared pair (`clearbuyId`) preselected as an overlay. */
export function labCompareHref(pdpHref: string, clearbuyId: number): string {
  const sep = pdpHref.includes('?') ? '&' : '?'
  return `${pdpHref}${sep}${LAB_COMPARE_PARAM}=${clearbuyId}#${LAB_ANCHOR}`
}

const cache = new Map<number, Promise<LabChartPaths | null>>()
/** Answers already in, read synchronously so a remounted `useCurvesFor` (the Compare body
 *  switching back in, the panel reopening, the phone sheet) draws on its first render instead
 *  of flashing "Loading". Failures are never kept here, as in `cache`. */
const resolved = new Map<number, LabChartPaths | null>()

/**
 * Resolves to `null` when the lab measured nothing for the id, and REJECTS when the request got
 * no answer (`ChartRequestError`). Only an answer is worth keeping for the session — a blip
 * must not blank every card, so a rejected fetch is forgotten and the next ask tries again.
 */
export function getCurves(clearbuyId: number): Promise<LabChartPaths | null> {
  let pending = cache.get(clearbuyId)
  if (!pending) {
    pending = loadLabChartsOrThrow(clearbuyId)
    cache.set(clearbuyId, pending)
    pending.then(
      loaded => resolved.set(clearbuyId, loaded),
      () => cache.delete(clearbuyId)
    )
  }
  return pending
}

/**
 * Curves for a lab id once `enabled` — `undefined` while loading (or not yet asked),
 * `null` when the RPC has nothing for it. A failed request is `null` here too: these callers
 * draw one pair and have no separate "did not load" state (`useCurvesFor` does).
 */
export function useCurves(
  clearbuyId: number | null | undefined,
  enabled: boolean
): LabChartPaths | null | undefined {
  const [curves, setCurves] = useState<LabChartPaths | null | undefined>(undefined)
  useEffect(() => {
    if (!enabled || clearbuyId === null || clearbuyId === undefined) {
      return undefined
    }
    let cancelled = false
    // A new lab id: back to "loading" so the previous pair's curve is never drawn under the
    // new name (useMdaqs does the same).
    setCurves(undefined)
    const load = async (): Promise<void> => {
      let loaded: LabChartPaths | null = null
      try {
        loaded = await getCurves(clearbuyId)
      } catch {
        loaded = null
      }
      if (!cancelled) {
        setCurves(loaded)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [clearbuyId, enabled])
  return curves
}

export interface CurvesFor {
  /** `undefined` while an id loads, `null` when the lab has nothing for it OR the request
   *  failed — `failed` tells the two apart. */
  curves: Record<number, LabChartPaths | null | undefined>
  /** Ids whose request got no answer (network, timeout, endpoint down). Not "no data": a
   *  caller must not label these unmeasured. */
  failed: ReadonlySet<number>
}

/**
 * Curves for a changing set of lab ids (the compare drawer's pins, the guide chart's selected
 * picks), each fetched once through the shared cache. Nothing is asked until `enabled`.
 */
export function useCurvesFor(ids: ReadonlyArray<number | null>, enabled = true): CurvesFor {
  const key = ids.filter((id): id is number => id !== null).join(',')
  const [map, setMap] = useState<Record<number, LabChartPaths | null | undefined>>(() => {
    const seed: Record<number, LabChartPaths | null> = {}
    if (enabled && key) {
      key.split(',').forEach(raw => {
        const id = Number(raw)
        const hit = resolved.get(id)
        if (hit !== undefined) {
          seed[id] = hit
        }
      })
    }
    return seed
  })
  const [failed, setFailed] = useState<ReadonlySet<number>>(() => new Set())
  useEffect(() => {
    const wanted = enabled && key ? key.split(',').map(Number) : []
    let cancelled = false
    wanted.forEach(id => {
      if (id in map) {
        return
      }
      getCurves(id)
        .then(loaded => {
          if (!cancelled) {
            setMap(prev => ({ ...prev, [id]: loaded }))
          }
        })
        .catch(() => {
          if (!cancelled) {
            setMap(prev => ({ ...prev, [id]: null }))
            setFailed(prev => new Set(prev).add(id))
          }
        })
    })
    return () => {
      cancelled = true
    }
    // The map is read to skip ids already fetched; re-running on its change would refetch nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled])
  return { curves: map, failed }
}

/**
 * True once the element has entered the viewport (sticky). No observer → true at once.
 * `active` re-arms the observer when the element mounts later than the hook (a block that
 * renders nothing until the declaration hydrates) — the ref alone can't signal that.
 */
export function useInView<T extends Element>(
  ref: RefObject<T>,
  active = true,
  margin = '200px'
): boolean {
  const [inView, setInView] = useState(false)
  const seen = useRef(false)
  useEffect(() => {
    const el = ref.current
    if (!el || !active || seen.current) {
      return undefined
    }
    if (typeof IntersectionObserver === 'undefined') {
      seen.current = true
      setInView(true)
      return undefined
    }
    const io = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          seen.current = true
          setInView(true)
          io.disconnect()
        }
      },
      { rootMargin: margin }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [ref, active, margin])
  return inView
}
